import barba from "@barba/core";
import barbaPrefetch from "@barba/prefetch";

class BarbaPjax {
  constructor(options = {}) {
    this.pageSelector = options.pageSelector || "#js-page";
    this.transitionSelector = options.transitionSelector || "#js-trans";

    this.containerSelector =
      options.containerSelector || '[data-barba="container"]';

    this.prefix = options.prefix || "--";

    this.namespaces = options.namespaces || [
      "home",
      "about",
      "contact",
      "fandom",
      "sound",
      "visual",
      "not-found",
    ];

    this.transitionClass = options.transitionClass || "is-trans";
    this.transitionEndClass = options.transitionEndClass || "is-trans-end";

    this.pageOutClass = options.pageOutClass || "--page-out";
    this.pageInClass = options.pageInClass || "--page-in";

    this.pageOutDuration = options.pageOutDuration || 700;
    this.pageInDuration = options.pageInDuration || 700;

    this.pageInTimer = null;

    this.init();
  }

  init() {
    this.disableScrollRestoration();
    this.updatePageClassByCurrentContainer();
    this.usePlugins();

    barba.init({
      preventRunning: true,

      transitions: [
        {
          name: "default-transition",

          once: (data) => {
            this.removeTransitionClasses();
            this.removePageTransitionClasses();
            this.updatePageClassByData(data);

            this.dispatchPageReadyNextFrame(data);
            this.playAutoplayVideosNextFrame(data.next.container);
          },

          leave: async (data) => {
            this.clearPageInTimer();

            // #js-trans に is-trans を付与
            this.addTransitionClass();

            // #js-page に --page-out を付与
            this.addPageOutClass();

            this.dispatchBeforeLeave(data);

            await this.wait(this.pageOutDuration);
          },

          beforeEnter: (data) => {
            this.updatePageClassByData(data);
          },

          enter: () => {
            this.scrollToTop();

            // #js-page に --page-in を付与
            this.addPageInClass();
          },

          afterEnter: (data) => {
            this.dispatchPageReadyNextFrame(data);
            this.playAutoplayVideosNextFrame(data.next.container);
          },
        },
      ],
    });
  }

  usePlugins() {
    barba.use(barbaPrefetch, {
      timeout: 2500,
      limit: 0,
    });
  }

  disableScrollRestoration() {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }

  scrollToTop() {
    window.scrollTo(0, 0);
  }

  wait(duration) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, duration);
    });
  }

  playAutoplayVideosNextFrame(container) {
    window.requestAnimationFrame(() => {
      this.playAutoplayVideos(container);
    });
  }

  playAutoplayVideos(container) {
    if (!container) return;

    const videos = container.querySelectorAll("video[autoplay]");

    if (!videos.length) return;

    videos.forEach((video) => {
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;

      video.setAttribute("muted", "");
      video.setAttribute("playsinline", "");
      video.setAttribute("webkit-playsinline", "");

      video.load();

      const playPromise = video.play();

      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {
          // ブラウザ側で自動再生が拒否された場合は何もしない
        });
      }
    });
  }

  getTransitionTarget() {
    return document.querySelector(this.transitionSelector);
  }

  addTransitionClass() {
    const transitionTarget = this.getTransitionTarget();

    if (!transitionTarget) return;

    // 次の遷移開始時に is-trans-end をリセット
    transitionTarget.classList.remove(this.transitionEndClass);

    // is-trans のアニメーションを連続遷移でも再発火
    transitionTarget.classList.remove(this.transitionClass);
    void transitionTarget.offsetWidth;
    transitionTarget.classList.add(this.transitionClass);
  }

  endTransitionClass() {
    const transitionTarget = this.getTransitionTarget();

    if (!transitionTarget) return;

    transitionTarget.classList.remove(this.transitionClass);

    // is-trans を削除するタイミングで is-trans-end を付与
    transitionTarget.classList.remove(this.transitionEndClass);
    void transitionTarget.offsetWidth;
    transitionTarget.classList.add(this.transitionEndClass);
  }

  removeTransitionClasses() {
    const transitionTarget = this.getTransitionTarget();

    if (!transitionTarget) return;

    transitionTarget.classList.remove(this.transitionClass);
    transitionTarget.classList.remove(this.transitionEndClass);
  }

  addPageOutClass() {
    const page = this.getPage();

    if (!page) return;

    page.classList.remove(this.pageInClass);
    page.classList.add(this.pageOutClass);
  }

  addPageInClass() {
    const page = this.getPage();

    if (!page) return;

    page.classList.remove(this.pageOutClass);

    /**
     * 連続遷移時にも --page-in のCSS animationを再発火させる
     */
    page.classList.remove(this.pageInClass);
    void page.offsetWidth;
    page.classList.add(this.pageInClass);

    this.clearPageInTimer();

    this.pageInTimer = window.setTimeout(() => {
      this.removePageInClass();

      // #js-trans から is-trans を削除し、is-trans-end を付与
      this.endTransitionClass();
    }, this.pageInDuration);
  }

  removePageInClass() {
    const page = this.getPage();

    if (!page) return;

    page.classList.remove(this.pageInClass);
  }

  removePageTransitionClasses() {
    const page = this.getPage();

    if (!page) return;

    page.classList.remove(this.pageOutClass);
    page.classList.remove(this.pageInClass);
  }

  clearPageInTimer() {
    if (!this.pageInTimer) return;

    window.clearTimeout(this.pageInTimer);
    this.pageInTimer = null;
  }

  dispatchPageReadyNextFrame(data) {
    window.requestAnimationFrame(() => {
      this.dispatchPageReady(data);
    });
  }

  getPage() {
    return document.querySelector(this.pageSelector);
  }

  getCurrentContainer() {
    return document.querySelector(this.containerSelector);
  }

  getNamespaceByContainer(container) {
    if (!container) return "";

    return container.getAttribute("data-barba-namespace") || "";
  }

  getNamespaceByData(data) {
    if (data && data.next && data.next.namespace) {
      return data.next.namespace;
    }

    if (data && data.next && data.next.container) {
      return this.getNamespaceByContainer(data.next.container);
    }

    return "";
  }

  updatePageClassByCurrentContainer() {
    const container = this.getCurrentContainer();
    const namespace = this.getNamespaceByContainer(container);

    this.updatePageClass(namespace);
  }

  updatePageClassByData(data) {
    const namespace = this.getNamespaceByData(data);

    this.updatePageClass(namespace);
  }

  updatePageClass(namespace) {
    const page = this.getPage();

    if (!page || !namespace) return;

    this.removePageClasses(page);

    page.classList.add(`${this.prefix}${namespace}`);
  }

  removePageClasses(page) {
    this.namespaces.forEach((namespace) => {
      page.classList.remove(`${this.prefix}${namespace}`);
    });
  }

  dispatchBeforeLeave(data) {
    window.dispatchEvent(
      new CustomEvent("barba:before-leave", {
        detail: data,
      }),
    );
  }

  dispatchPageReady(data) {
    window.dispatchEvent(
      new CustomEvent("barba:page-ready", {
        detail: data,
      }),
    );
  }
}

export default BarbaPjax;
